/**
 * Where you're signed in — Settings → Security. Every active session, the
 * current one first; any other can be signed out, or all of them at once.
 */
import { Loader2 } from '@tonnie/assets';
import { useTranslation } from '@tonnie/locale';
import { Button, SettingsCard } from '@tonnie/ui';
import { useState } from 'react';
import { toast } from 'sonner';

import { useActiveSessions } from './hooks/use-active-sessions';
import { SessionRow } from './SessionRow';

export function SessionsCard() {
  const { t } = useTranslation();
  const { sessions, isLoading, error, revokeSession, revokeOthers } = useActiveSessions({
    notify: toast,
  });
  const [signingOut, setSigningOut] = useState(false);
  const others = sessions.filter((s) => !s.isCurrent).length;

  return (
    <SettingsCard
      title={t('auth.security.sessions.title')}
      description={t('auth.security.sessions.description')}
      data-testid="settings-sessions"
      action={
        others > 0 ? (
          <Button
            variant="outline"
            size="sm"
            disabled={signingOut}
            onClick={async () => {
              setSigningOut(true);
              await revokeOthers();
              setSigningOut(false);
            }}
            data-testid="settings-sessions-sign-out-others"
          >
            {signingOut && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" aria-hidden="true" />}
            {t('auth.security.sessions.signOutOthers')}
          </Button>
        ) : undefined
      }
    >
      {isLoading ? (
        <li className="flex justify-center px-6 py-6" role="status">
          <Loader2
            className="h-5 w-5 animate-spin text-muted-foreground"
            aria-label={t('common.loading')}
          />
        </li>
      ) : error ? (
        <li role="alert" className="px-6 py-4 text-sm text-destructive-accessible">
          {error}
        </li>
      ) : (
        <>
          {sessions.map((session) => (
            <SessionRow key={session.id} session={session} onRevoke={revokeSession} />
          ))}
          {others === 0 && (
            <li className="px-6 py-3 text-sm text-muted-foreground">
              {t('auth.security.sessions.none')}
            </li>
          )}
        </>
      )}
    </SettingsCard>
  );
}
